import { BiasAnalysis } from '@/types/common';

/**
 * Generates a formatted markdown report for bias analysis results
 * @param content The bias analysis content object
 * @returns A markdown string containing the formatted report
 */
export const prepareMarkdown = (content: {
    currentText: string;
    normalizedSensitivity: number;
    biasAnalysis: BiasAnalysis;
}): string => {
    try {
        // Extract the needed data
        const { currentText, normalizedSensitivity, biasAnalysis } = content;
        const { biasedWords = [], labeledText = {} } = biasAnalysis;

        // Format the current date
        const currentDate = new Date().toLocaleDateString();

        // Generate each section
        const textSection = generateTextSection(currentText);
        const sensitivitySection = generateSensitivitySection(
            normalizedSensitivity
        );
        const biasedWordsSection = generateBiasedWordsSection(biasedWords);
        const labeledTextSection = generateLabeledTextSection(labeledText);
        const analysisSection = generateAnalysisSection(
            currentText,
            biasedWords,
            labeledText
        );

        // Combine all sections into the final markdown
        return `# Bias Analysis Report

${textSection}

${sensitivitySection}

${biasedWordsSection}

${labeledTextSection}

${analysisSection}

---
*Report generated on ${currentDate}*`;
    } catch (error) {
        console.error('Error generating bias analysis markdown:', error);
        return generateErrorReport(error);
    }
};

/**
 * Generates the text section of the report
 */
const generateTextSection = (text: string): string => {
    return `## 1. Analyzed Text
"${text}"`;
};

/**
 * Generates the sensitivity score section
 */
const generateSensitivitySection = (sensitivity: number): string => {
    return `## 2. Sensitivity Score
**Normalized Sensitivity**: ${sensitivity}`;
};

/**
 * Generates the biased words table section
 */
const generateBiasedWordsSection = (biasedWords: any[]): string => {
    if (biasedWords.length === 0) {
        return `## 3. Identified Biased Words

No biased words identified.`;
    }

    const tableRows = biasedWords
        .map(
            (b) =>
                `| ${b.word}   | ${b.index}     | ${b.labels.join(
                    ', '
                )}   | ${b.reasons.join(', ')} |`
        )
        .join('\n');

    return `## 3. Identified Biased Words

| Word    | Index | Labels           | Reasons                        |
|---------|-------|------------------|---------------------------------|
${tableRows}`;
};

/**
 * Generates the labeled text section
 */
const generateLabeledTextSection = (
    labeledText: Record<string, string[]>
): string => {
    if (Object.keys(labeledText).length === 0) {
        return `## 4. Labeled Text by Categories

No labeled text categories identified.`;
    }

    const categories = Object.entries(labeledText)
        .map(
            ([category, words]) =>
                `**${category}**: ${(words as string[]).join(', ')}`
        )
        .join('\n\n');

    return `## 4. Labeled Text by Categories

${categories}`;
};

/**
 * Generates the analysis section
 */
const generateAnalysisSection = (
    text: string,
    biasedWords: any[],
    labeledText: Record<string, string[]>
): string => {
    return `## 5. Analysis Summary
${generateSummary(text, biasedWords, labeledText)}`;
};

/**
 * Generates an error report
 */
const generateErrorReport = (error: unknown): string => {
    return `# Bias Analysis Report

An error occurred while generating the report: ${error}

*Report attempted on ${new Date().toLocaleDateString()}*`;
};

/**
 * Helper function to generate a summary based on the bias analysis
 * This would be expanded with more sophisticated logic in a real implementation
 */
export const generateSummary = (
    text: string,
    biasedWords: any[],
    labeledText: Record<string, string[]>
): string => {
    const summaryChecks = [
        // Check for assumptions
        {
            condition: hasLabeledTextCategory(labeledText, 'ASSUMP'),
            message: 'Makes assumptions about groups',
        },
        // Check for generalizations
        {
            condition: hasLabeledTextCategory(labeledText, 'GEN'),
            message: 'Contains generalizations about groups',
        },
        // Check for racial references
        {
            condition: containsAnyKeyword(text, [
                'black',
                'white',
                'asian',
                'hispanic',
            ]),
            message: 'Contains references to racial or ethnic groups',
        },
        // Check for comparative language
        {
            condition: containsAnyKeyword(text, [
                'not like',
                'unlike',
                'compared to',
            ]),
            message: 'Uses comparative language that may reinforce stereotypes',
        },
        // Check for "actually" which often signals an exception to a stereotype
        {
            condition: text.includes('actually'),
            message: 'Implies an exception to a stereotype',
        },
    ];

    const summaryPoints = summaryChecks
        .filter((check) => check.condition)
        .map((check) => check.message);

    // If no specific issues were identified, provide a generic message
    if (summaryPoints.length === 0) {
        return 'No significant bias patterns identified in the analyzed text.';
    }

    return summaryPoints.join('\n- ');
};

/**
 * Helper function to check if labeled text contains a specific category
 */
const hasLabeledTextCategory = (
    labeledText: Record<string, string[]>,
    category: string
): boolean => {
    return Boolean(labeledText[category]?.length > 0);
};

/**
 * Helper function to check if text contains any of the specified keywords
 */
const containsAnyKeyword = (text: string, keywords: string[]): boolean => {
    const lowerText = text.toLowerCase();
    return keywords.some((keyword) => lowerText.includes(keyword));
};
